import { useMemo, useState } from "react";
import type { AdminGalleryAlbum, AdminGalleryPhoto } from "@tih/shared";
import { GalleryPhotosModal } from "./GalleryPhotosModal";
import { GalleryPhotosUploadDropzone } from "./GalleryPhotosUploadDropzone";

type Props = {
  album: AdminGalleryAlbum;
  photos: AdminGalleryPhoto[];
  photosLoading: boolean;
  modalOpen: boolean;
  onModalOpenChange: (open: boolean) => void;
  onUpdate: (patch: { published?: boolean; coverUrl?: string | null }) => void;
  onRemovePhoto: (photoId: number) => void;
  onError: (message: string | null) => void;
};

function sortPhotosWithCoverFirst(
  photos: AdminGalleryPhoto[],
  coverUrl: string | null,
): AdminGalleryPhoto[] {
  if (!coverUrl) return photos;
  return [...photos].sort((a, b) => {
    const aCover = a.url === coverUrl;
    const bCover = b.url === coverUrl;
    if (aCover !== bCover) return aCover ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });
}

export function GalleryAlbumUploadPanel({
  album,
  photos,
  photosLoading,
  modalOpen,
  onModalOpenChange,
  onUpdate,
  onRemovePhoto,
  onError,
}: Props) {
  const [open, setOpen] = useState(true);
  const coverUrl = album.coverUrl ?? null;
  const sortedPhotos = useMemo(
    () => sortPhotosWithCoverFirst(photos, coverUrl),
    [photos, coverUrl],
  );

  const photoSummary = photosLoading
    ? "Loading photos…"
    : photos.length === 0
      ? "No photos in this album yet."
      : `${photos.length} ${photos.length === 1 ? "photo" : "photos"}`;

  return (
    <div className="admin-gallery-upload-section admin-event-details">
      <button
        type="button"
        className="admin-event-details-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>Photos</span>
        <span className="admin-event-details-toggle-hint">{photoSummary}</span>
        <span className="admin-event-details-chevron">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="admin-event-details-body admin-gallery-photos-accordion-body">
          {photosLoading ? (
            <p className="admin-muted">Loading photos…</p>
          ) : (
            <>
              <GalleryPhotosUploadDropzone
                album={album}
                onError={onError}
                className="admin-gallery-photos-dropzone admin-gallery-photos-dropzone--inline"
              />
              {photos.length > 0 && (
                <div className="admin-gallery-photo-grid">
                  {sortedPhotos.map((p) => {
                    const isCover = coverUrl != null && coverUrl === p.url;
                    return (
                      <figure
                        key={p.id}
                        className={`admin-gallery-photo-tile${isCover ? " admin-gallery-photo-tile--cover" : ""}`}
                      >
                        <div className="admin-gallery-photo-tile-btn">
                          <img src={p.thumbUrl ?? p.url} alt="" loading="lazy" decoding="async" />
                          {isCover && (
                            <span className="admin-gallery-photo-cover-badge">Cover</span>
                          )}
                        </div>
                      </figure>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <GalleryPhotosModal
        open={modalOpen}
        onClose={() => onModalOpenChange(false)}
        album={album}
        photos={photos}
        photosLoading={photosLoading}
        onUpdate={onUpdate}
        onRemovePhoto={onRemovePhoto}
        onError={onError}
      />
    </div>
  );
}
